import { useCallback, useEffect, useRef, useState } from 'react'
import { Mail, X } from 'lucide-react'
import { Alert } from '@/components/ds2'
import { EmailAccount } from 'pages/dashboard/pages/dispatcher/pages/dispatcher-emails/controllers/types'
import { handleGmailEmailAdd, handleMicrosoftEmailAdd, handleNylasEmailAdd, setEmailProviderPickerInstance } from 'utils/email-connect'
import aicpaSocLogo from 'assets/images/aicpa-soc-logo.png'

type ShowToastFunction = (message: string, type: 'success' | 'danger') => void

interface PickerCallbacks {
    onSuccess?: (accounts: EmailAccount[]) => void
    onError?: (error: string) => void
    showToast?: ShowToastFunction
    title?: string
    subtitle?: string
    errorMessage?: string
    loginHint?: string
}

const GoogleGlyph = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
        <path
            fill="#FFC107"
            d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
        />
        <path
            fill="#FF3D00"
            d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
        />
        <path
            fill="#4CAF50"
            d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
        />
        <path
            fill="#1976D2"
            d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
        />
    </svg>
)

const MicrosoftGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
)

const providerButtonClass =
    'flex h-12 w-full items-center justify-center gap-2.5 rounded-3xl bg-default text-[15px] font-medium text-default-foreground transition-colors duration-150 hover:bg-default-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface'

export const EmailProviderPickerModal = () => {
    const [open, setOpen] = useState(false)
    const callbacksRef = useRef<PickerCallbacks | null>(null)

    const show = useCallback((callbacks: PickerCallbacks) => {
        callbacksRef.current = callbacks
        setOpen(true)
    }, [])

    useEffect(() => {
        setEmailProviderPickerInstance({ show })
        return () => setEmailProviderPickerInstance(null)
    }, [show])

    const close = () => {
        setOpen(false)
        callbacksRef.current = null
    }

    const handleSelect = (provider: 'gmail' | 'microsoft' | 'other') => {
        const callbacks = callbacksRef.current
        close()

        if (provider === 'gmail') {
            handleGmailEmailAdd(callbacks?.onSuccess, callbacks?.onError, callbacks?.showToast, callbacks?.loginHint)
        } else if (provider === 'microsoft') {
            handleMicrosoftEmailAdd(callbacks?.onSuccess, callbacks?.onError, callbacks?.showToast, callbacks?.loginHint)
        } else {
            handleNylasEmailAdd(callbacks?.onSuccess, callbacks?.onError, callbacks?.showToast)
        }
    }

    const handleCancel = () => {
        const callbacks = callbacksRef.current
        close()
        callbacks?.onError?.('')
    }

    if (!open) return null

    const title = callbacksRef.current?.title ?? 'Connect Email'
    const subtitle = callbacksRef.current?.subtitle ?? 'Choose your email provider to connect your account.'
    const errorMessage = callbacksRef.current?.errorMessage

    return (
        // This app-level modal must clear ds2 drawers (surface z-9999) such as the Box Truck load drawer.
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={handleCancel}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="relative flex w-full max-w-3xl overflow-hidden rounded-3xl bg-surface text-foreground shadow-overlay animate-in fade-in zoom-in duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Left: provider choices */}
                <div className="flex-1 px-10 py-11">
                    <h3 className="text-2xl font-bold text-foreground">{title}</h3>
                    <p className={`${errorMessage ? 'mb-4' : 'mb-7'} mt-2.5 text-sm leading-relaxed text-foreground-muted`}>{subtitle}</p>

                    {errorMessage && (
                        <div className="mb-5">
                            <Alert variant="danger" title={errorMessage} description="The email was not sent. Reconnect the account, then try again." />
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <button onClick={() => handleSelect('gmail')} className={providerButtonClass}>
                            <GoogleGlyph />
                            Enable with Google
                        </button>
                        <button onClick={() => handleSelect('microsoft')} className={providerButtonClass}>
                            <MicrosoftGlyph />
                            Enable with Microsoft
                        </button>
                    </div>

                    <div className="my-5 flex items-center gap-3">
                        <div className="h-px flex-1 bg-background-tertiary" />
                        <span className="text-[13px] font-medium text-foreground-muted">OR</span>
                        <div className="h-px flex-1 bg-background-tertiary" />
                    </div>

                    <button
                        onClick={() => handleSelect('other')}
                        className="flex h-12 w-full items-center justify-center gap-2.5 rounded-3xl border border-border bg-transparent text-[15px] font-medium text-foreground transition-colors duration-150 hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                    >
                        <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
                        Other email service (Yahoo, etc.)
                    </button>
                </div>

                {/* Right: AICPA SOC trust panel */}
                <div className="hidden w-[320px] max-w-[44%] flex-col items-center justify-center bg-accent-soft px-7 py-10 text-center sm:flex">
                    <img src={aicpaSocLogo} alt="AICPA SOC for Service Organizations" className="mb-5 h-[168px] w-[168px] object-contain" />
                    <p className="mb-2.5 text-sm leading-relaxed text-foreground">We use industry-standard encryption to keep your credentials safe</p>
                    <a
                        href="https://www.aicpa-cima.com/resources/landing/system-and-organization-controls-soc-suite-of-services"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[13px] font-medium text-foreground underline"
                    >
                        Learn more ↗
                    </a>
                </div>

                <button
                    onClick={handleCancel}
                    className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-xl bg-default text-foreground-muted transition-colors duration-150 hover:bg-default-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                    aria-label="Close"
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    )
}
